import React, { useState, useEffect, useCallback } from "react";
import styles from "./DealerDeliveryRanges.module.scss";
import Grid from "@mui/material/Grid";
import { TextValidator, ValidatorForm } from "react-material-ui-form-validator";
import ValidationRules from "../../../../common/ValidationRules";
import { Button } from "@mui/material";
import { withTranslation } from "react-i18next";
import DeliverRangeAndPriceRow from "./deliveryRangesRow/DealerDeliveryRangesRow";
import MultiToggleWidget from "../../../../shared/toggles/multiToggleWidget/MultiToggleWidget";
import EditIcon from "../../../../shared/Edit/EditIcon";
import { logMsg } from "../../../../common/Logger";
import { DELIVERY_BANDS } from "../../../../common/Constants";
import PendingActionPrompt from "../../../../shared/pendingActionPrompt/PendingActionPrompt";
import DeliveryRangesClient from "../../../../shared/clients/DeliveryRangesClient";
import PendingActionClient from "../../../../shared/clients/PendingActionClient";
import UpperCaseText from "../../../../utils/UpperCaseText";
import HasPermissionTo from "../../../../common/HasPermissionTo";

function DealerDeliveryRanges({ localizedInfo, user, hideOrShow, getPendingActions, t }) {
  const [deliveryData, setDeliveryData] = useState({});
  const [isEditActive, setIsEditActive] = useState(false);
  const [enableSaveButton, setEnableSaveButton] = useState(true);
  const [dataLoaded, setDataLoaded] = useState(false);
  const [pendingActionPrompt, setPendingActionPrompt] = useState(false);
  const [pendingActionStatusForDealers, setPendingActionStatusForDealers] = useState([]);
  const [disablePendingActionPrompt, setDisablePendingActionPrompt] = useState(false);
  const [dataToSendToDialog, setDataToSendToDialog] = useState(null);

  const currencySymbol = localizedInfo?.defaultCurrency?.symbol || "NA";
  const distanceUnit = localizedInfo?.defaultDistanceUnit || "NA";
  const onboardedDealers = user.getOnboardedDealerGroup(true);

  const copyDeliveryData = useCallback(() => {
    return JSON.parse(JSON.stringify(deliveryData));
  }, [deliveryData]);

  const determinePAStatus = useCallback(() => {
    const statusForDealers = user.getPendingActionStatusForDealers(
      user.pendingActions,
      "DELIVERY_BANDS"
    );
    setPendingActionStatusForDealers(statusForDealers);
    setDisablePendingActionPrompt(statusForDealers.length === 0);
  }, [user]);

  const getDeliveryRanges = useCallback(() => {
    let foundFirstFalse = false;
    hideOrShow(true);

    DeliveryRangesClient.getDealerDeliveryRanges(user)
      .then((data) => {
        data.nonMainlandDeliveryCharge = data.nonMainlandDeliveryCharge
          ? data.nonMainlandDeliveryCharge.toFixed(2)
          : "0";
        data.deliverInternationalCharge = data.deliverInternationalCharge
          ? data.deliverInternationalCharge.toFixed(2)
          : "0";

        data.deliveryRangeOptions.forEach((range) => {
          range.value = range.value.toFixed(2);
          if (!range.acceptedByDealer) {
            if (foundFirstFalse) {
              range.disabled = true;
            } else {
              range.disabled = false;
              foundFirstFalse = true;
            }
          } else {
            range.disabled = false;
          }
        });

        setDeliveryData(data);
      })
      .catch((err) => {
        logMsg("err", err);
      })
      .finally(() => {
        hideOrShow(false);
        setDataLoaded(true);
      });
  }, [user, hideOrShow]);

  const saveDeliveryRanges = useCallback((data, commonIds, dealerIds) => {
    DeliveryRangesClient.saveDealerDeliveryRanges(data, user, commonIds)
      .then(() => {
        PendingActionClient.removeDealerPendingAction(
          user,
          dealerIds,
          DELIVERY_BANDS,
          1
        ).then(() => {
          getDeliveryRanges();
          getPendingActions().then(() => {
            determinePAStatus();
          });
        });

        setIsEditActive(false);
        setEnableSaveButton(false);
        setPendingActionPrompt(false);
      })
      .catch(() => {});
  }, [user, getDeliveryRanges, getPendingActions, determinePAStatus]);

  const handlePriceChange = useCallback((e, index) => {
    const newDeliveryData = copyDeliveryData();
    newDeliveryData.deliveryRangeOptions[index].value = e.target.value;
    setDeliveryData(newDeliveryData);
    setEnableSaveButton(true);
  }, [copyDeliveryData]);

  const handleDeliveryChargeChange = useCallback((e) => {
    const newDeliveryData = copyDeliveryData();
    newDeliveryData.nonMainlandDeliveryCharge = e.target.value;
    setDeliveryData(newDeliveryData);
    setEnableSaveButton(true);
  }, [copyDeliveryData]);

  const handleInternationalCharge = useCallback((e) => {
    const newDeliveryData = copyDeliveryData();
    newDeliveryData.deliverInternationalCharge = e.target.value;
    setDeliveryData(newDeliveryData);
    setEnableSaveButton(true);
  }, [copyDeliveryData]);

  const handleSave = useCallback(() => {
    const newDeliveryData = copyDeliveryData();
    newDeliveryData.deliveryRangeOptions = deliveryData.deliveryRangeOptions.filter(
      (range) => {
        if (range.acceptedByDealer) {
          return range;
        }
      }
    );

    setDataToSendToDialog(newDeliveryData);

    if (
      user.showAll &&
      onboardedDealers.length > 1 &&
      !disablePendingActionPrompt
    ) {
      setPendingActionPrompt(true);
    } else {
      saveDeliveryRanges(
        newDeliveryData,
        user.getCommonIdsWithoutSDLR(),
        user.getDealerIdsWithoutSDLR()
      );
    }
  }, [copyDeliveryData, deliveryData.deliveryRangeOptions, user, onboardedDealers.length, disablePendingActionPrompt, saveDeliveryRanges]);

  const deliverInNonMainLandToggle = useCallback(() => {
    const newDeliveryData = copyDeliveryData();
    newDeliveryData.nonMainlandDelivery = !deliveryData.nonMainlandDelivery;
    setDeliveryData(newDeliveryData);
    setEnableSaveButton(true);
  }, [copyDeliveryData, deliveryData.nonMainlandDelivery]);

  const sellInternationalToggle = useCallback(() => {
    const newDeliveryData = copyDeliveryData();
    newDeliveryData.sellInternational = !deliveryData.sellInternational;
    newDeliveryData.deliverInternational = false;
    setDeliveryData(newDeliveryData);
    setEnableSaveButton(true);
  }, [copyDeliveryData, deliveryData.sellInternational]);

  const deliverInternationalToggle = useCallback(() => {
    const newDeliveryData = copyDeliveryData();
    newDeliveryData.deliverInternational = !deliveryData.deliverInternational;
    setDeliveryData(newDeliveryData);
    setEnableSaveButton(true);
  }, [copyDeliveryData, deliveryData.deliverInternational]);

  const updateToggleStatus = useCallback((index) => {
    const newDeliveryData = copyDeliveryData();
    const deliveryRanges = newDeliveryData.deliveryRangeOptions;
    deliveryRanges[index].acceptedByDealer = !deliveryData.deliveryRangeOptions[index].acceptedByDealer;

    if (!deliveryRanges[index].acceptedByDealer) {
      for (let i = parseInt(index) + 1; i < deliveryRanges.length; ++i) {
        deliveryRanges[i].acceptedByDealer = false;
        deliveryRanges[i].disabled = true;
      }
    } else {
      if (parseInt(index) + 1 < deliveryRanges.length) {
        deliveryRanges[parseInt(index) + 1].disabled = false;
      }
    }

    newDeliveryData.deliveryRangeOptions = deliveryRanges;
    setDeliveryData(newDeliveryData);
    setEnableSaveButton(true);
  }, [copyDeliveryData, deliveryData.deliveryRangeOptions]);

  const pendingActionPromptClose = useCallback(() => {
    setPendingActionPrompt(false);
  }, []);

  const pendingActionPromptApplyAll = useCallback(() => {
    const commonIds = pendingActionStatusForDealers[0].map((dealer) => dealer.commonId);
    const dealerIds = pendingActionStatusForDealers[0].map((dealer) => dealer.dealerId);

    saveDeliveryRanges(
      dataToSendToDialog,
      commonIds.toString().split("|").join("%7C"),
      dealerIds
    );
  }, [pendingActionStatusForDealers, dataToSendToDialog, saveDeliveryRanges]);

  const pendingActionPromptApplySelective = useCallback(() => {
    const commonIds = pendingActionStatusForDealers[0]
      .filter((dealer) => !dealer.pendingActionStatus)
      .map((dealer) => dealer.commonId);
    const dealerIds = pendingActionStatusForDealers[0]
      .filter((dealer) => !dealer.pendingActionStatus)
      .map((dealer) => dealer.dealerId);

    saveDeliveryRanges(
      dataToSendToDialog,
      commonIds.toString().split("|").join("%7C"),
      dealerIds
    );
  }, [pendingActionStatusForDealers, dataToSendToDialog, saveDeliveryRanges]);

  const displayToggle = useCallback((toggleName, status, isDisabled, callBack) => {
    const disabled = isDisabled ||
      user.readOnly ||
      !user.permissions.rolePermissions.hasOwnProperty("deliveryRanges:write");

    return (
      <div>
        <MultiToggleWidget
          settings={{
            acceptedText: t("GeneralEMP.statusYes"),
            rejectedText: t("GeneralEMP.statusNo"),
            suppClass: `${disabled ? "" : styles.toggleWrapper}`,
          }}
          isToggleDisabled={disabled}
          toggleName={toggleName}
          currentStatus={status ? "yes" : "no"}
          onSelect={() => callBack()}
        />
      </div>
    );
  }, [user.readOnly, user.permissions.rolePermissions, t, styles.toggleWrapper]);

  useEffect(() => {
    ValidationRules();
    determinePAStatus();
    getDeliveryRanges();
  }, [determinePAStatus, getDeliveryRanges]);

  return (
    dataLoaded && (
      <section className="pageWrapper">
        <div
          id="businessSettingsHeader"
          className={`${styles.businessSettings} pagePaddingRight pagePaddingLeft`}
        >
          <p>{t("dealerDeliveryBands.header1")}</p>
          <p>{t("dealerDeliveryBands.header2")}</p>
        </div>
        <div
          id="businessSettingsSubHeader"
          className={styles.headerBackGround}
        >
          <span className={`${styles.taskHeader} pagePaddingLeft`}>
            {`${UpperCaseText(user.market)} ${t("dealerDeliveryBands.mainlandBands")}`}
          </span>
          <span className={`${styles.taskDetails} pagePaddingLeft`}>
            {t("dealerDeliveryBands.headerNote")}
          </span>
        </div>

        <ValidatorForm onSubmit={handleSave}>
          <div className="pagePaddingLeft pagePaddingRight">
            <Grid
              id="deliveryRangeTableHeader"
              container
              className={styles.divider}
            >
              <Grid item md={4}>
                <div className={styles.headerWrap}>
                  <span>
                    {t("dealerDeliveryBands.distance") + "  (" + distanceUnit + ")"}{" "}
                  </span>
                </div>
              </Grid>

              <Grid item md={4} className={styles.center}>
                <div className={styles.headerWrap}>
                  {t("dealerDeliveryBands.delivery")}
                </div>
              </Grid>
              <Grid
                item
                md={4}
                className={`${styles.rowHeader} ${styles.right}`}
              >
                {isEditActive || user.readOnly ? (
                  <div className={styles.headerWrap}>
                    {t("dealerDeliveryBands.deliveryCharge") + "  (" + currencySymbol + ")"}
                  </div>
                ) : (
                  <HasPermissionTo
                    perform={["deliveryRanges:write"]}
                    permissions={user.permissions.rolePermissions}
                    render={() => {
                      return (
                        <div
                          className={styles.headerWrap}
                          data-testid="editCharge"
                        >
                          {t("dealerDeliveryBands.deliveryCharge")}
                          <EditIcon
                            id="Ranges"
                            onClick={() => {
                              setIsEditActive(!isEditActive);
                              setEnableSaveButton(true);
                            }}
                          />
                        </div>
                      );
                    }}
                    noRender={() => {
                      return (
                        <div className={styles.headerWrap}>
                          {t("dealerDeliveryBands.deliveryCharge")}
                        </div>
                      );
                    }}
                  />
                )}
              </Grid>
            </Grid>
          </div>

          <div
            id="deliveryRangesRow"
            className={`${styles.tableBottom} pagePaddingLeft pagePaddingRight`}
          >
            {deliveryData.deliveryRangeOptions &&
              deliveryData.deliveryRangeOptions.map((item, index) => {
                return (
                  <DeliverRangeAndPriceRow
                    key={index}
                    rowNo={index}
                    dataRow={item}
                    editActive={isEditActive}
                    currency={currencySymbol}
                    index={index}
                    displayToggle={displayToggle}
                    updateToggleStatus={updateToggleStatus}
                    onPriceChange={(e, index) => handlePriceChange(e, index)}
                  />
                );
              })}
          </div>

          <div
            id="doUDeliverHeader"
            className={`${styles.subSectionBackGround} pagePaddingLeft pagePaddingRight`}
          >
            <div>
              <p className={styles.taskHeader}>
                {user.market.toUpperCase() === "CHE"
                  ? t("dealerDeliveryBands.doUDeliverHeaderChe")
                  : t("dealerDeliveryBands.doUDeliverHeader")}
              </p>
              <p className={styles.taskDetails}>
                {t("dealerDeliveryBands.doUDeliverBody")}
              </p>
            </div>
            {displayToggle(
              "deliverInUK",
              deliveryData.nonMainlandDelivery,
              false,
              deliverInNonMainLandToggle
            )}
          </div>

          {deliveryData.nonMainlandDelivery && (
            <div
              id="doUDeliverDiv"
              className={`${styles.subSectionPriceBackGround} pagePaddingLeft pagePaddingRight`}
            >
              <span className={styles.headerWrap}>
                {" "}
                {t("dealerDeliveryBands.delveryChargeNational") + "  (" + currencySymbol + ")"}{" "}
                &nbsp;
              </span>
              {isEditActive ? (
                <TextValidator
                  style={{ borderRadius: "0px" }}
                  name="nonMainlandDelivery"
                  variant="outlined"
                  value={
                    deliveryData.nonMainlandDeliveryCharge === null
                      ? ""
                      : deliveryData.nonMainlandDeliveryCharge
                  }
                  onChange={handleDeliveryChargeChange}
                  validators={[
                    "required",
                    "minNumber:0",
                    "isNumber",
                    "isNumericWithTwoDecimalPlaces",
                  ]}
                  errorMessages={[
                    t("nscDeliveryBands.required"),
                    t("nscDeliveryBands.invalid"),
                    t("nscDeliveryBands.invalid"),
                    t("nscDeliveryBands.invalid"),
                  ]}
                />
              ) : (
                <p>{deliveryData.nonMainlandDeliveryCharge}</p>
              )}
            </div>
          )}

          <div
            id="doUSellHeader"
            className={`${styles.subSectionBackGround} pagePaddingLeft pagePaddingRight`}
          >
            <div>
              <p className={`${styles.taskHeader}`}>
                {t("dealerDeliveryBands.doUSellHeader")}
              </p>
              <p className={`${styles.taskDetails}`}>
                {t("dealerDeliveryBands.doUSellBody")}
              </p>
            </div>
            {displayToggle(
              "sellInternational",
              deliveryData.sellInternational,
              false,
              sellInternationalToggle
            )}
          </div>

          {deliveryData.sellInternational && (
            <div
              id="doUDeliverInterHeader"
              className={`${styles.subSectionBackGround} pagePaddingLeft pagePaddingRight`}
            >
              <div>
                <p className={`${styles.taskHeader}`}>
                  {t("dealerDeliveryBands.doUDeliverInHeader")}
                </p>
                <p className={`${styles.taskDetails}`}>
                  {t("dealerDeliveryBands.doUDeliverInBody")}
                </p>
              </div>
              {displayToggle(
                "deliverInternational",
                deliveryData.deliverInternational,
                false,
                deliverInternationalToggle
              )}
            </div>
          )}

          {deliveryData.deliverInternational && deliveryData.sellInternational && (
            <div
              id="doUDeliverInterDiv"
              className={`${styles.subSectionPriceBackGround} pagePaddingLeft pagePaddingRight`}
            >
              <span className={styles.headerWrap}>
                {" "}
                {t("dealerDeliveryBands.delveryChargeInterNational") + "  (" + currencySymbol + ")"}{" "}
                &nbsp;
              </span>
              {isEditActive ? (
                <TextValidator
                  style={{ borderRadius: "0px" }}
                  name="deliverInternationalText"
                  variant="outlined"
                  value={
                    deliveryData.deliverInternationalCharge === null
                      ? ""
                      : deliveryData.deliverInternationalCharge
                  }
                  onChange={handleInternationalCharge}
                  validators={[
                    "required",
                    "minNumber:0",
                    "isNumber",
                    "isNumericWithTwoDecimalPlaces",
                  ]}
                  errorMessages={[
                    t("nscDeliveryBands.required"),
                    t("nscDeliveryBands.invalid"),
                    t("nscDeliveryBands.invalid"),
                    t("nscDeliveryBands.invalid"),
                  ]}
                />
              ) : (
                <p>{deliveryData.deliverInternationalCharge}</p>
              )}
            </div>
          )}

          <div className={styles.actionSection}>
            {enableSaveButton ? (
              <HasPermissionTo
                perform={["deliveryRanges:write"]}
                permissions={user.permissions.rolePermissions}
                render={() => {
                  return (
                    <Button
                      type="submit"
                      id="savePricing"
                      className={styles.saveBtn}
                      data-testid="savePricing"
                      disabled={user.readOnly}
                      component="button"
                    >
                      {t("dealerDeliveryBands.save")}
                    </Button>
                  );
                }}
              />
            ) : (
              <span className={styles.pricesApplied}>
                {t("dealerDeliveryBands.pricesApplied")}
              </span>
            )}
          </div>
        </ValidatorForm>

        <PendingActionPrompt
          localizedInfo={localizedInfo}
          user={user}
          hideOrShow={hideOrShow}
          getPendingActions={getPendingActions}
          t={t}
          disabled={disablePendingActionPrompt}
          dealersActioned={
            disablePendingActionPrompt
              ? []
              : pendingActionStatusForDealers[0]?.filter(
              (dealer) => dealer.pendingActionStatus
            ) || []
          }
          dealersNotActioned={
            disablePendingActionPrompt
              ? []
              : pendingActionStatusForDealers[0]?.filter(
              (dealer) => !dealer.pendingActionStatus
            ) || []
          }
          title={t("PendingActionPrompt.deliveryRanges")}
          buttonTitle={t("PendingActionPrompt.deliveryRangesButton")}
          onClose={pendingActionPromptClose}
          open={pendingActionPrompt}
          applyAll={pendingActionPromptApplyAll}
          applySome={pendingActionPromptApplySelective}
        />
      </section>
    )
  );
}

export default withTranslation("emp")(DealerDeliveryRanges);
